import * as React from "react";
import { Box, Chip, IconButton, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { SxProps, Theme } from "@mui/material/styles";
import { GridApiCommunity } from "@mui/x-data-grid/internals";
import { StandaloneExport, CustomExportProps } from "../customExport";

export const COACHING_PAGE_HEADER_HEIGHT = 76;

interface CoachingPageHeaderProps {
  title: string;
  chipLabel?: string;
  subtitle?: string;
  /** Renders an ArrowBack IconButton on the left when provided */
  onBack?: () => void;
  rightContent?: React.ReactNode;
  sx?: SxProps<Theme>;
  /** Pass a grid apiRef + csv/print options to render an Export button in the header */
  exportProps?: {
    apiRef: React.MutableRefObject<GridApiCommunity>;
    csvOptions?: CustomExportProps["csvOptions"];
    printOptions?: CustomExportProps["printOptions"];
  };
}

export const CoachingPageHeader: React.FC<CoachingPageHeaderProps> = ({
  title,
  chipLabel,
  subtitle,
  onBack,
  rightContent,
  exportProps,
  sx,
}) => {
  return (
    <Box
      sx={{
        backgroundColor: "#ffffffff",
        width: "100%",
        px: 3,
        overflow: "hidden",
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        minHeight: `${COACHING_PAGE_HEADER_HEIGHT}px`,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        ...sx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {onBack && (
            <IconButton
              onClick={onBack}
              size="small"
              sx={{ color: "text.secondary", mr: 1 }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                {title}
              </Typography>
              {chipLabel && (
                <Chip
                  label={chipLabel}
                  size="small"
                  sx={{
                    backgroundColor: "#DBEAFE",
                    color: "#1E40AF",
                    fontWeight: 500,
                    fontSize: "0.875rem",
                  }}
                />
              )}
            </Box>
            {subtitle && (
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        {(rightContent || exportProps) && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {rightContent}
            {exportProps && (
              <StandaloneExport
                apiRef={exportProps.apiRef}
                csvOptions={exportProps.csvOptions}
                printOptions={exportProps.printOptions}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
